import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Home, Calendar, MessageCircle, Trophy, Compass, Grid3x3 } from "lucide-react";
import { MoreSheet } from "@/components/MoreSheet";

const NAV_TABS = [
  { to: "/today", label: "Today", icon: Home, testId: "tab-today" },
  { to: "/plan", label: "Plan", icon: Calendar, testId: "tab-plan" },
  { to: "/talk", label: "Talk", icon: MessageCircle, testId: "tab-talk" },
  // Batch 40 — Activities took Memories' bottom-bar slot. Memories moved
  // into the More sheet. Dedicated "what do we need to drive to" surface.
  { to: "/activities", label: "Activities", icon: Trophy, testId: "tab-activities" },
  // Batch 46.1 — Whereabouts (formerly Find My / Locator). 5th NavTab so
  // the family map is one tap away from anywhere in the app.
  { to: "/whereabouts", label: "Whereabouts", icon: Compass, testId: "tab-whereabouts" },
];

/**
 * TabBar — fixed to the bottom of the canvas. Hides automatically when the
 * keyboard is open (`hide-when-kb` reacts to `data-kb="open"` on <html>),
 * so composers can dock flush against the keyboard without overlap.
 *
 * The last slot is a "More" launcher (button, NOT a NavLink) that opens
 * a 2-column grid sheet of secondary destinations (You, Recipes, Settings,
 * plus coming-soon Places / Meal Planner / Vacations / Find My / Insights).
 */
export function TabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <>
      <nav
        data-testid="tab-bar"
        className="hide-when-kb fixed bottom-0 left-0 right-0 bg-background border-t border-border/70 z-40"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
          maxWidth: "480px",
          margin: "0 auto",
        }}
      >
        <ul className="flex justify-around items-stretch h-[52px]">
          {NAV_TABS.map(({ to, label, icon: Icon, testId }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                data-testid={testId}
                className={({ isActive }) =>
                  [
                    "relative flex flex-col items-center justify-center gap-1 h-full text-[10px] tap font-medium transition-colors px-0.5",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-primary"
                      />
                    )}
                    <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.2 : 1.7} />
                    <span className="leading-none truncate max-w-full">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
          {/* MORE launcher — button, not NavLink. Opens MoreSheet. */}
          <li className="flex-1">
            <button
              data-testid="tab-more"
              onClick={() => setMoreOpen(true)}
              className={[
                "relative w-full flex flex-col items-center justify-center gap-1 h-full text-[10px] tap font-medium transition-colors px-0.5",
                moreOpen ? "text-primary" : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {moreOpen && (
                <span
                  aria-hidden
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-primary"
                />
              )}
              <Grid3x3 className="w-[22px] h-[22px]" strokeWidth={moreOpen ? 2.2 : 1.7} />
              <span className="leading-none truncate max-w-full">More</span>
            </button>
          </li>
        </ul>
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
